import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["status", "loginButton", "friendPanel", "chatLink", "loading", "details"]
  static values = {
    liffId: String,
    sessionUrl: String,
    requestToken: String,
    destination: String,
    entry: String,
    addFriendUrl: String
  }

  connect() {
    this.active = true
    this.autoFriendshipAttempted = false
    this.waitingForFriend = false
    this.completed = false
    this.onVisibilityChange = () => this.resumeAfterFriendship()
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    return this.initializeLiff()
  }

  disconnect() {
    this.active = false
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
  }

  async resumeAfterFriendship() {
    if (document.visibilityState !== "visible" || !this.waitingForFriend || this.requestingFriendship) return
    try {
      await this.establishSession()
    } catch (_error) {
      this.showStatus("友だち追加の確認ができませんでした。もう一度お試しください。")
    }
  }

  async initializeLiff() {
    if (!this.liffIdValue || !window.liff) {
      this.showStatus("LINEアプリからもう一度お試しください。")
      return
    }

    try {
      await window.liff.init({
        liffId: this.liffIdValue,
        withLoginOnExternalBrowser: this.entryValue === "web"
      })
      if (!this.active) return
      if (!window.liff.isLoggedIn()) {
        this.showStatus("LINEにログインして続けてください。")
        this.loginButtonTarget.hidden = false
        return
      }

      await this.establishSession()
    } catch (_error) {
      this.showStatus("LINEの申込情報を確認できませんでした。しばらくしてから、もう一度お試しください。")
      if (this.active && !window.liff?.isLoggedIn?.()) this.loginButtonTarget.hidden = false
    }
  }

  login() {
    window.liff?.login?.({ redirectUri: window.location.href })
  }

  async establishSession() {
    if (!this.active || this.establishing || this.completed) return
    this.establishing = true
    let body
    try {
      body = await this.fetchSession()
    } finally {
      this.establishing = false
    }
    if (!this.active || !body) return

    if (body.state === "ready" && body.redirect_url) {
      this.completed = true
      this.waitingForFriend = false
      this.friendPanelTarget.hidden = true
      if (this.entryValue !== "web") {
        this.loadingTarget.hidden = false
        this.detailsTarget.hidden = true
        window.location.replace(body.redirect_url)
        return
      }
      this.showStatus("LINE連携が完了しました。トークを開きます。")
      this.chatLinkTarget.href = body.redirect_url
      this.chatLinkTarget.hidden = false
      if (this.entryValue === "web" && body.redirect_url.startsWith("https://line.me/") && window.liff.isInClient?.()) {
        try {
          window.liff.openWindow({ url: body.redirect_url, external: true })
        } catch (_error) {
          this.showStatus("LINE連携が完了しました。下のボタンからトークを開いてください。")
        }
      } else {
        window.location.replace(body.redirect_url)
      }
    } else if (body.state === "friend_required") {
      this.waitingForFriend = true
      this.showStatus("申込との連携ができました。友だち追加すると、受付メッセージをLINEでお届けします。")
      this.friendPanelTarget.hidden = false
      if (this.entryValue === "web" && !this.autoFriendshipAttempted) {
        this.autoFriendshipAttempted = true
        await this.requestFriendship({ automatic: true })
      }
    } else if (body.state === "unlinked") {
      this.waitingForFriend = false
      this.showStatus("連携中のお申し込みがありません。申込完了画面のLINEボタンから、もう一度お試しください。")
    } else {
      this.showStatus(body.error || "申込情報を確認できませんでした。")
    }
  }

  async fetchSession() {
    const idToken = window.liff?.getIDToken?.()
    if (!idToken) throw new Error("Missing LINE ID token")

    const friend = await this.friendFlag()
    const payload = {
      id_token: idToken,
      request_token: this.requestTokenValue || null,
      destination: this.destinationValue || "menu",
      entry: this.entryValue || null
    }
    if (friend !== null) payload.friend = friend

    const response = await fetch(this.sessionUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
      },
      body: JSON.stringify(payload)
    })
    const body = await response.json()

    if (!response.ok) {
      this.showStatus(body.error || "LINEの申込情報を確認できませんでした。")
      return
    }

    return body
  }

  async friendFlag() {
    if (!window.liff?.getFriendship) return null

    try {
      return (await window.liff.getFriendship()).friendFlag === true
    } catch (_error) {
      return null
    }
  }

  async requestFriendship({ automatic = false } = {}) {
    if (this.requestingFriendship || this.completed) return
    this.autoFriendshipAttempted = true
    this.requestingFriendship = true
    try {
      if (window.liff?.isInClient?.() && window.liff?.requestFriendship) {
        try {
          await window.liff.requestFriendship()
        } catch (_error) {
          // Unsupported clients retain a manual add-friend link.
          if (!automatic && this.active) this.openFriendPage()
          return
        }
        if (this.active) await this.establishSession()
      } else if (!automatic) {
        this.openFriendPage()
      }
    } catch (_error) {
      this.showStatus("友だち追加の確認ができませんでした。もう一度お試しください。")
    } finally {
      this.requestingFriendship = false
    }
  }

  openFriendPage() {
    if (!this.addFriendUrlValue) return
    if (window.liff?.isInClient?.() && window.liff?.openWindow) {
      window.liff.openWindow({ url: this.addFriendUrlValue, external: true })
    } else {
      window.location.assign(this.addFriendUrlValue)
    }
  }

  showStatus(message) {
    if (!this.active) return
    this.statusTarget.textContent = message
    this.loadingTarget.hidden = true
    this.detailsTarget.hidden = false
  }
}
